/* ==========================================================================
   Cart drawer (slide-in cart)
   Shell:   snippets/cart-drawer.liquid
   Content: sections/cart-drawer.liquid (cart), sections/cart-drawer-recs.liquid
            (Frequently Bought Together for the product just added)
   Styles:  assets/cart-drawer.css

   What it does
   - Takes over every <form action="/cart/add"> submit on the page (all
     product templates share the same form markup, and any other form that
     posts to /cart/add is covered too), posts it to /cart/add.js, and opens
     the drawer with the fresh cart instead of redirecting to /cart.
   - Watches fetch/XHR calls other scripts make to /cart/add (apps, page
     builders, the FBT widget) and opens the drawer after those as well.
   - Quantity stepper, Remove, Checkout, View cart.
   - Keeps the small-order handling fee line in step with the merchandise
     subtotal (same rule as templates/cart.liquid) after every change and
     right before Checkout.
   - Loads the Frequently Bought Together block for the added product via
     /products/<handle>?section_id=cart-drawer-recs (cached; prefetched on
     product pages) and lets the customer add those with one tap.

   Public API: window.sbCartDrawer = { open, close, refresh, itemAdded }
   (namespaced: the Free Gifts app already defines window.CartDrawer)
   Events:     'cart-drawer:updated' (detail: {itemCount, total}),
               'cart-drawer:added'   (detail: {items})
   No jQuery dependency.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-cart-drawer]');
  if (!root || root.hasAttribute('data-cart-drawer-ready')) return;
  root.setAttribute('data-cart-drawer-ready', '');

  var SECTION_ID = 'cart-drawer';
  var RECS_SECTION_ID = 'cart-drawer-recs';
  var MARK_HEADER = 'X-Cart-Drawer';
  var QTY_DEBOUNCE = 350;

  var rootUrl = root.getAttribute('data-root-url') || '/';
  if (rootUrl.charAt(rootUrl.length - 1) !== '/') rootUrl += '/';

  var panel = root.querySelector('.cart-drawer__panel');
  var header = root.querySelector('[data-drawer-header]');
  var body = root.querySelector('[data-drawer-body]');
  var recsSlot = root.querySelector('[data-drawer-recs]');
  var titleEl = root.querySelector('[data-drawer-title]');
  var countEl = root.querySelector('[data-drawer-count]');
  var checkEl = root.querySelector('[data-drawer-check]');
  var errorEl = root.querySelector('[data-drawer-error]');

  var state = {
    itemCount: 0,
    total: 0,
    productIds: [],
    hf: { enabled: false, variant: 0, threshold: 0, all: false },
    heading: 'Your cart',
    headingAdded: 'Added to cart',
    recsEnabled: true,
    lastRecsHandle: null
  };
  var recsCache = {};
  var isOpen = false;
  var lastFocus = null;
  var qtyTimers = {};
  var ownRequestUntil = 0;

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */
  function jsonFetch(url, opts) {
    opts = opts || {};
    opts.credentials = 'same-origin';
    var headers = { 'Accept': 'application/json' };
    headers[MARK_HEADER] = '1';
    if (!(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json;charset=UTF-8';
    opts.headers = headers;
    return fetch(url, opts).then(function (res) {
      return res.json().then(function (json) {
        return { ok: res.ok, status: res.status, body: json };
      }, function () {
        return { ok: res.ok, status: res.status, body: null };
      });
    });
  }

  function sectionUrl(path, id) {
    return path + (path.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + id;
  }

  function parseSection(html, id) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.getElementById('shopify-section-' + id) || doc.body;
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }
  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  function errorMessage(body, fallback) {
    if (body && typeof body === 'object') {
      return body.description || body.message || fallback;
    }
    return fallback;
  }

  /* ---------------------------------------------------------------------
     Render: move section parts into the shell
     --------------------------------------------------------------------- */
  function hydrate(src) {
    if (!src) return;
    var st = src.querySelector('[data-cart-drawer-state]');
    if (st) {
      state.itemCount = parseInt(st.getAttribute('data-item-count'), 10) || 0;
      state.total = parseInt(st.getAttribute('data-total'), 10) || 0;
      state.productIds = (st.getAttribute('data-product-ids') || '').split(',').filter(Boolean);
      state.hf = {
        enabled: st.getAttribute('data-hf-enabled') === 'true',
        variant: parseInt(st.getAttribute('data-hf-variant'), 10) || 0,
        threshold: parseInt(st.getAttribute('data-hf-threshold'), 10) || 0,
        all: st.getAttribute('data-hf-all') === 'true'
      };
      state.heading = st.getAttribute('data-heading') || state.heading;
      state.headingAdded = st.getAttribute('data-heading-added') || state.headingAdded;
      state.recsEnabled = st.getAttribute('data-recs-enabled') !== 'false';
    }
    var parts = src.querySelectorAll('[data-drawer-part]');
    Array.prototype.forEach.call(parts, function (part) {
      var name = part.getAttribute('data-drawer-part');
      var slot = root.querySelector('[data-drawer-part="' + name + '"]');
      if (slot) slot.innerHTML = part.innerHTML;
    });
    root.classList.toggle('is-empty', state.itemCount === 0);
    if (state.itemCount === 0 && recsSlot) recsSlot.innerHTML = '';
    updateCount();
    updateBadges();
    updateRecsState();
    document.dispatchEvent(new CustomEvent('cart-drawer:updated', {
      detail: { itemCount: state.itemCount, total: state.total }
    }));
  }

  function applySectionHtml(html) {
    if (!html) return;
    hydrate(parseSection(html, SECTION_ID));
  }

  function refresh() {
    return fetch(sectionUrl(rootUrl, SECTION_ID), { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(applySectionHtml)
      .catch(function () {});
  }

  function updateCount() {
    if (countEl) countEl.textContent = state.itemCount > 0 ? '(' + state.itemCount + ')' : '';
  }

  function updateBadges() {
    var badges = document.querySelectorAll('.cart-count');
    Array.prototype.forEach.call(badges, function (el) {
      el.textContent = state.itemCount;
      el.classList.toggle('hidden-count', state.itemCount === 0);
    });
  }

  function setAddedTitle(added) {
    if (titleEl) titleEl.textContent = added ? state.headingAdded : state.heading;
    if (checkEl) checkEl.hidden = !added;
  }

  /* ---------------------------------------------------------------------
     Open / close, focus, scroll lock
     --------------------------------------------------------------------- */
  function focusables() {
    var sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(panel.querySelectorAll(sel), function (el) {
      return el.offsetParent !== null;
    });
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) { e.preventDefault(); panel.focus(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    clearError();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-lock');
    document.addEventListener('keydown', onKeydown);
    if (body) body.scrollTop = 0;
    setTimeout(function () { panel.focus({ preventScroll: true }); }, 60);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-lock');
    document.removeEventListener('keydown', onKeydown);
    setTimeout(function () { if (!isOpen) setAddedTitle(false); }, 350);
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    }
    lastFocus = null;
  }

  // Mobile: swipe the header down to dismiss the sheet.
  (function swipeToClose() {
    if (!header) return;
    var startY = null;
    header.addEventListener('touchstart', function (e) {
      startY = e.touches && e.touches.length ? e.touches[0].clientY : null;
    }, { passive: true });
    header.addEventListener('touchmove', function (e) {
      if (startY === null || !e.touches || !e.touches.length) return;
      if (e.touches[0].clientY - startY > 70) { startY = null; close(); }
    }, { passive: true });
    header.addEventListener('touchend', function () { startY = null; }, { passive: true });
  })();

  /* ---------------------------------------------------------------------
     Handling fee: keep the fee line correct (mirrors templates/cart.liquid)
     Resolves with fresh section HTML when the cart was changed, else null.
     --------------------------------------------------------------------- */
  function feeWanted(cart) {
    var hf = state.hf;
    var merch = 0;
    cart.items.forEach(function (it) {
      if (it.variant_id !== hf.variant) merch += it.final_line_price;
    });
    return (hf.enabled && merch > 0 && (hf.all || merch < hf.threshold)) ? 1 : 0;
  }

  function feeCurrent(cart) {
    return cart.items.reduce(function (n, it) {
      return it.variant_id === state.hf.variant ? n + it.quantity : n;
    }, 0);
  }

  function syncFee(cart) {
    if (!state.hf.variant) return Promise.resolve(null);
    var p = cart ? Promise.resolve(cart) : jsonFetch(rootUrl + 'cart.js').then(function (r) { return r.ok ? r.body : null; });
    return p.then(function (c) {
      if (!c || !c.items) return null;
      var want = feeWanted(c);
      if (feeCurrent(c) === want) return null;
      var updates = {};
      updates[state.hf.variant] = want;
      return jsonFetch(rootUrl + 'cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ updates: updates, sections: SECTION_ID })
      }).then(function (u) {
        return (u.ok && u.body && u.body.sections) ? u.body.sections[SECTION_ID] : null;
      });
    }).catch(function () { return null; });
  }

  /* ---------------------------------------------------------------------
     Added-to-cart flow
     --------------------------------------------------------------------- */
  function itemsFromAddResponse(resBody) {
    if (!resBody || typeof resBody !== 'object') return [];
    if (Array.isArray(resBody.items)) return resBody.items;
    if (resBody.id || resBody.variant_id) return [resBody];
    return [];
  }

  function trackAdded(items) {
    if (!items.length) return;
    document.dispatchEvent(new CustomEvent('cart-drawer:added', { detail: { items: items } }));
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'USD',
          value: items.reduce(function (sum, it) { return sum + (it.final_line_price || it.line_price || 0); }, 0) / 100,
          items: items.map(function (it) {
            return {
              item_id: String(it.variant_id || it.id),
              item_name: it.product_title || it.title,
              item_variant: it.variant_title || undefined,
              price: (it.final_price || it.price || 0) / 100,
              quantity: it.quantity || 1
            };
          })
        }
      });
    } catch (e) { /* analytics must never break the cart */ }
  }

  // body: the /cart/add.js response (may carry `sections`).
  function itemAdded(resBody) {
    var items = itemsFromAddResponse(resBody);
    var handle = items.length && items[0].handle ? items[0].handle : null;
    var html = resBody && resBody.sections && resBody.sections[SECTION_ID];
    var p = html
      ? Promise.resolve(html)
      : fetch(sectionUrl(rootUrl, SECTION_ID), { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : ''; });
    return p.then(function (h) {
      applySectionHtml(h);
      setAddedTitle(true);
      open();
      trackAdded(items);
      if (handle) loadRecs(handle);
      return syncFee(null);
    }).then(function (h2) {
      if (h2) applySectionHtml(h2);
    }).catch(function () {
      refresh().then(open);
    });
  }

  /* ---------------------------------------------------------------------
     Product form takeover (every <form action="/cart/add"> on the page)
     --------------------------------------------------------------------- */
  function isCartAddUrl(u) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(u || ''));
  }

  function formButton(form, submitter) {
    if (submitter && submitter.tagName) return submitter;
    return form.querySelector('button[type="submit"], input[type="submit"], button[name="add"]');
  }

  function setFormBusy(btn, busy, done) {
    if (!btn) return;
    var textEl = btn.querySelector('[id^="addToCartText"]');
    if (busy) {
      if (!btn.hasAttribute('data-cd-label')) {
        btn.setAttribute('data-cd-label', textEl ? textEl.textContent : (btn.value || btn.textContent));
      }
      btn.classList.add('cart-drawer-adding');
      btn.setAttribute('aria-busy', 'true');
      if (textEl) textEl.textContent = 'Adding…';
      else if (btn.tagName === 'INPUT') btn.value = 'Adding…';
      return;
    }
    var label = btn.getAttribute('data-cd-label') || '';
    btn.classList.remove('cart-drawer-adding');
    btn.removeAttribute('aria-busy');
    var restore = function () {
      if (textEl) textEl.textContent = label;
      else if (btn.tagName === 'INPUT') btn.value = label;
      btn.removeAttribute('data-cd-label');
    };
    if (done) {
      if (textEl) textEl.textContent = 'Added ✓';
      else if (btn.tagName === 'INPUT') btn.value = 'Added ✓';
      setTimeout(restore, 1600);
    } else {
      restore();
    }
  }

  function showFormError(form, msg) {
    clearFormError(form);
    var el = document.createElement('div');
    el.className = 'errors cart-drawer-form-error';
    el.setAttribute('role', 'alert');
    el.textContent = msg;
    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn && btn.parentNode) btn.parentNode.insertBefore(el, btn.nextSibling);
    else form.appendChild(el);
  }
  function clearFormError(form) {
    var old = form.querySelectorAll('.cart-drawer-form-error');
    Array.prototype.forEach.call(old, function (el) { el.parentNode.removeChild(el); });
  }

  function submitForm(form, submitter) {
    var btn = formButton(form, submitter);
    if (btn && btn.classList.contains('cart-drawer-adding')) return;
    clearFormError(form);
    var fd = new FormData(form);
    fd.append('sections', SECTION_ID);
    setFormBusy(btn, true);
    ownRequestUntil = Date.now() + 2000;

    jsonFetch(rootUrl + 'cart/add.js', { method: 'POST', body: fd })
      .then(function (res) {
        if (!res.ok) {
          showFormError(form, errorMessage(res.body, 'Could not add to cart. Please try again.'));
          setFormBusy(btn, false, false);
          return;
        }
        setFormBusy(btn, false, true);
        return itemAdded(res.body);
      })
      .catch(function () {
        showFormError(form, 'Could not add to cart. Please try again.');
        setFormBusy(btn, false, false);
      });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!isCartAddUrl(form.getAttribute('action'))) return;
    if (form.hasAttribute('data-cart-drawer-ignore')) return;
    if (e.defaultPrevented) return; // another script owns this form; its request is picked up below
    e.preventDefault();
    submitForm(form, e.submitter);
  });

  /* ---------------------------------------------------------------------
     Other scripts adding to the cart (apps, page builders, FBT widget):
     watch fetch/XHR for /cart/add and open the drawer after they succeed.
     --------------------------------------------------------------------- */
  function onExternalAdd(resBody) {
    if (Date.now() < ownRequestUntil) return;
    itemAdded(resBody);
  }

  function hasMark(headers) {
    if (!headers) return false;
    if (typeof headers.get === 'function') return !!headers.get(MARK_HEADER);
    if (Array.isArray(headers)) return headers.some(function (h) { return h[0] === MARK_HEADER; });
    return !!(headers[MARK_HEADER] || headers[MARK_HEADER.toLowerCase()]);
  }

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var marked = hasMark(init && init.headers) || hasMark(input && input.headers);
      var p = nativeFetch.apply(this, arguments);
      if (isCartAddUrl(url) && !marked) {
        p.then(function (res) {
          if (!res.ok) return;
          res.clone().json().then(onExternalAdd, function () { onExternalAdd(null); });
        }, function () {});
      }
      return p;
    };
  }

  (function patchXhr() {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto) return;
    var nativeOpen = proto.open;
    var nativeSend = proto.send;
    proto.open = function (method, url) {
      this.__cdUrl = url;
      return nativeOpen.apply(this, arguments);
    };
    proto.send = function () {
      if (isCartAddUrl(this.__cdUrl)) {
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;
          var parsed = null;
          try { parsed = JSON.parse(this.responseText); } catch (e) { /* HTML response */ }
          onExternalAdd(parsed);
        });
      }
      return nativeSend.apply(this, arguments);
    };
  })();

  /* ---------------------------------------------------------------------
     Line changes: stepper, remove
     --------------------------------------------------------------------- */
  var pendingEdits = {};

  function changeLine(itemEl, qty) {
    var line = parseInt(itemEl.getAttribute('data-line'), 10);
    if (!line) return Promise.resolve();
    itemEl.classList.add('is-updating');
    clearError();
    return jsonFetch(rootUrl + 'cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ line: line, quantity: qty, sections: SECTION_ID })
    }).then(function (res) {
      if (!res.ok) {
        showError(errorMessage(res.body, 'Could not update the cart.'));
        return refresh();
      }
      var cart = res.body;
      applySectionHtml(cart && cart.sections && cart.sections[SECTION_ID]);
      return syncFee(cart).then(function (h2) { if (h2) applySectionHtml(h2); });
    }).catch(function () {
      showError('Could not update the cart.');
      refresh();
    });
  }

  function lineKey(itemEl) {
    return itemEl.getAttribute('data-key') || itemEl.getAttribute('data-line');
  }

  function flushEdit(itemEl) {
    var key = lineKey(itemEl);
    clearTimeout(qtyTimers[key]);
    delete qtyTimers[key];
    delete pendingEdits[key];
    var input = itemEl.querySelector('[data-cart-qty-input]');
    if (!input) return Promise.resolve();
    return changeLine(itemEl, clampQty(input.value));
  }

  function scheduleQty(itemEl) {
    var key = lineKey(itemEl);
    clearTimeout(qtyTimers[key]);
    pendingEdits[key] = itemEl;
    qtyTimers[key] = setTimeout(function () { flushEdit(itemEl); }, QTY_DEBOUNCE);
  }

  function clampQty(v) {
    var q = parseInt(v, 10);
    if (isNaN(q) || q < 0) q = 0;
    return q;
  }

  root.addEventListener('click', function (e) {
    var t = e.target;

    if (t.closest('[data-cart-drawer-close]')) { e.preventDefault(); close(); return; }

    var stepBtn = t.closest('[data-cart-qty-minus], [data-cart-qty-plus]');
    if (stepBtn) {
      e.preventDefault();
      var item = stepBtn.closest('.cart-drawer__item');
      var input = item && item.querySelector('[data-cart-qty-input]');
      if (!input) return;
      var q = clampQty(input.value);
      q = stepBtn.hasAttribute('data-cart-qty-plus') ? q + 1 : Math.max(0, q - 1);
      input.value = q;
      scheduleQty(item);
      return;
    }

    var removeBtn = t.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      var removeItem = removeBtn.closest('.cart-drawer__item');
      if (removeItem) changeLine(removeItem, 0);
      return;
    }

    var checkoutBtn = t.closest('[data-cart-checkout]');
    if (checkoutBtn) {
      e.preventDefault();
      goToCheckout(checkoutBtn);
      return;
    }

    var recBtn = t.closest('[data-rec-add]');
    if (recBtn) {
      e.preventDefault();
      addRecommendation(recBtn);
    }
  });

  root.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty-input]');
    if (input) {
      input.value = clampQty(input.value);
      var item = input.closest('.cart-drawer__item');
      if (item) scheduleQty(item);
      return;
    }
    var select = e.target.closest('[data-rec-select]');
    if (select) {
      var card = select.closest('[data-rec]');
      var opt = select.options[select.selectedIndex];
      var priceEl = card && card.querySelector('[data-rec-price]');
      var addBtn = card && card.querySelector('[data-rec-add]');
      if (priceEl && opt) priceEl.textContent = opt.getAttribute('data-price') || priceEl.textContent;
      if (addBtn) addBtn.setAttribute('data-variant-id', select.value);
    }
  });

  root.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target.closest('[data-cart-qty-input]')) return;
    e.preventDefault();
    var item = e.target.closest('.cart-drawer__item');
    if (item) flushEdit(item);
  });

  function goToCheckout(btn) {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    // Flush quantity edits still inside the debounce window, settle the
    // handling fee, then go. Each edit goes through /cart/change.js by line
    // number (one at a time, since a re-render renumbers the lines).
    var edits = Object.keys(pendingEdits).map(function (k) { return pendingEdits[k]; });
    var p = Promise.resolve();
    edits.forEach(function (itemEl) {
      p = p.then(function () {
        var fresh = itemEl.isConnected ? itemEl : root.querySelector('.cart-drawer__item[data-key="' + itemEl.getAttribute('data-key') + '"]');
        return fresh ? flushEdit(fresh) : null;
      }).catch(function () {});
    });
    p.then(function () { return syncFee(null); })
      .catch(function () {})
      .then(function () { window.location.href = rootUrl + 'checkout'; });
  }

  /* ---------------------------------------------------------------------
     Frequently bought together
     --------------------------------------------------------------------- */
  function getRecsHtml(handle) {
    if (recsCache[handle]) return recsCache[handle];
    var p = fetch(sectionUrl(rootUrl + 'products/' + encodeURIComponent(handle), RECS_SECTION_ID), { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(function (html) {
        if (!html) return '';
        var sec = parseSection(html, RECS_SECTION_ID);
        return sec ? sec.innerHTML.trim() : '';
      })
      .catch(function () { return ''; });
    recsCache[handle] = p;
    return p;
  }

  function loadRecs(handle) {
    if (!recsSlot) return;
    if (!state.recsEnabled || !handle) { recsSlot.innerHTML = ''; return; }
    state.lastRecsHandle = handle;
    getRecsHtml(handle).then(function (html) {
      if (state.lastRecsHandle !== handle) return;
      recsSlot.innerHTML = html || '';
      // Products already in the cart when the block first shows are left out.
      var cards = recsSlot.querySelectorAll('[data-rec]');
      Array.prototype.forEach.call(cards, function (card) {
        if (state.productIds.indexOf(card.getAttribute('data-product-id')) !== -1) card.hidden = true;
      });
      updateRecsState();
    });
  }

  function updateRecsState() {
    if (!recsSlot) return;
    var block = recsSlot.querySelector('[data-cart-drawer-recs]');
    if (!block) return;
    var visible = 0;
    var cards = block.querySelectorAll('[data-rec]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.hidden) return;
      visible++;
      var inCart = state.productIds.indexOf(card.getAttribute('data-product-id')) !== -1;
      var btn = card.querySelector('[data-rec-add]');
      var label = card.querySelector('[data-rec-add-label]');
      var select = card.querySelector('[data-rec-select]');
      if (btn) {
        btn.disabled = inCart;
        btn.classList.toggle('is-added', inCart);
        btn.classList.remove('is-adding');
        if (label) label.textContent = inCart ? 'Added ✓' : 'Add';
      }
      if (select) select.disabled = inCart;
    });
    block.hidden = visible === 0;
  }

  function addRecommendation(btn) {
    if (btn.disabled || btn.classList.contains('is-adding')) return;
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;
    var label = btn.querySelector('[data-rec-add-label]');
    btn.classList.add('is-adding');
    if (label) label.textContent = 'Adding…';
    clearError();
    ownRequestUntil = Date.now() + 2000;
    jsonFetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: SECTION_ID })
    }).then(function (res) {
      if (!res.ok) {
        btn.classList.remove('is-adding');
        if (label) label.textContent = 'Add';
        showError(errorMessage(res.body, 'Could not add that item.'));
        return;
      }
      applySectionHtml(res.body && res.body.sections && res.body.sections[SECTION_ID]);
      setAddedTitle(true);
      trackAdded(itemsFromAddResponse(res.body));
      return syncFee(null).then(function (h2) { if (h2) applySectionHtml(h2); });
    }).catch(function () {
      btn.classList.remove('is-adding');
      if (label) label.textContent = 'Add';
      showError('Could not add that item.');
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  var staticSection = document.getElementById('shopify-section-' + SECTION_ID);
  if (staticSection) {
    hydrate(staticSection);
    staticSection.innerHTML = '';
    staticSection.hidden = true;
  }
  setAddedTitle(false);

  // Product pages: warm the FBT block for this product so it is instant on add.
  var pageHandle = root.getAttribute('data-product-handle');
  if (pageHandle && state.recsEnabled) {
    var idle = window.requestIdleCallback || function (cb) { setTimeout(cb, 1200); };
    idle(function () { getRecsHtml(pageHandle); });
  }

  // Theme editor: re-hydrate when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.id === 'shopify-section-' + SECTION_ID) {
      hydrate(e.target);
      e.target.innerHTML = '';
      e.target.hidden = true;
    }
  });

  window.sbCartDrawer = {
    open: function () { refresh().then(open); },
    close: close,
    refresh: refresh,
    itemAdded: itemAdded
  };
})();
